<template>

<!-- Company Profile Editor – Tailwind HTML Mock -->
<!-- Assumes your Tailwind config tokens: bg-card, border-border, brand, newPrimary, etc. -->
<!-- Page container -->
<div class="min-h-screen bg-background font-inter text-foreground">
  <!-- Sticky header / actions -->
  <header class="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur">
    <div class="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between gap-4">
      <div class="flex items-center gap-3">
        <div>
          <h1 class="text-base font-semibold leading-tight">Company Profile Editor</h1>
          <!-- Autosave status (aria-live for screen readers) -->
          <p class="text-xs text-muted-foreground" aria-live="polite">
            {{ statusLabel }}
          </p>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <button
          v-if="hasDraft"
          class="px-3 py-2 text-sm rounded-md border border-border hover:bg-muted disabled:opacity-60 disabled:cursor-not-allowed"
          @click="handleDiscardClick"
          :disabled="isDiscarding || isPublishing || isLoading || isAutosavePending"
        >
          <span v-if="!isDiscarding">Discard Draft</span>
          <span v-else class="flex items-center gap-2">
            <Loader2 class="h-4 w-4 animate-spin" />
            Discarding…
          </span>
        </button>
        <button
          class="px-3 py-2 text-sm rounded-md bg-brand text-white hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
          @click="handlePublish"
          :disabled="!canPublish"
        >
          <span v-if="!isPublishing">Publish</span>
          <span v-else class="flex items-center gap-2">
            <Loader2 class="h-4 w-4 animate-spin" />
            Publishing…
          </span>
        </button>
      </div>
    </div>
  </header>

  <!-- Content grid: sticky nav + editor canvas -->
  <div class="mx-auto max-w-7xl px-4 py-8 grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-8">
    <!-- Sticky Section Nav (left) -->
    <nav
      v-if="isChecklistReady"
      class="lg:sticky lg:top-[72px] self-start space-y-6"
    >
      <!-- Profile completeness -->
      <section class="rounded-lg border border-border bg-card p-4">
        <div class="flex items-center justify-between">
          <h2 id="nav-completion" class="text-sm font-semibold">Profile completeness</h2>
          <span class="text-xs text-muted-foreground">
            {{ isChecklistReady ? `${checklistProgress}%` : '…' }}
          </span>
        </div>
        <div class="mt-3 h-2 w-full rounded-full bg-muted/40 overflow-hidden">
          <div
            class="h-full bg-newPrimary transition-all duration-300"
            :style="{ width: isChecklistReady ? `${checklistProgress}%` : '0%' }"
          ></div>
        </div>
        <template v-if="isChecklistReady">
          <ul class="mt-3 space-y-2 text-xs">
            <li
              v-for="section in checklistSections"
              :key="section.id"
              class="flex flex-col gap-1"
            >
              <div class="flex items-center gap-2">
                <component
                  :is="section.complete ? CheckCircle2 : Circle"
                  class="h-4 w-4"
                  :class="section.complete ? 'text-brand' : 'text-muted-foreground'"
                />
                <span class="text-sm font-medium" :class="section.complete ? 'text-foreground' : 'text-muted-foreground'">
                  {{ section.label }}
                </span>
              </div>

              <ul
                v-if="!section.complete"
                class="ml-6 space-y-1"
              >
                <li
                  v-for="item in section.items.filter((item) => !item.complete)"
                  :key="item.id"
                  class="flex items-start gap-3 text-muted-foreground"
                >
                  <component
                    :is="item.required ? AlertCircle : Circle"
                    class="h-4 w-4 flex-shrink-0 mt-[1px]"
                    :class="item.required
                      ? 'text-red-500'
                      : isPublishAttempted && item.message
                        ? 'text-red-500'
                        : 'text-muted-foreground'"
                  />
                  <div class="space-y-1">
                    <span class="text-[11px] font-medium text-foreground">{{ item.label }}</span>
                    <p
                      v-if="isPublishAttempted && item.message"
                      class="text-[11px] text-red-600"
                    >
                      {{ item.message }}
                    </p>
                    <p
                      v-else-if="item.description"
                      class="text-[11px] text-muted-foreground"
                    >
                      {{ item.description }}
                    </p>
                  </div>
                </li>
              </ul>
            </li>
          </ul>
        </template>
        <p v-else-if="industryProfileStore.error" class="mt-4 text-[11px] text-red-600">{{ industryProfileStore.error }}</p>
        <p v-else class="mt-4 text-[11px] text-muted-foreground">Loading checklist…</p>
      </section>

      <!-- Anchored links -->
      <section class="rounded-lg border border-border bg-card p-2">
        <ul class="space-y-1">
          <li><a href="#sec-details" class="block rounded-md px-3 py-2 text-sm hover:bg-muted aria-[current=page]:bg-muted" aria-current="page">Company Details</a></li>
          <li><a href="#sec-profile" class="block rounded-md px-3 py-2 text-sm hover:bg-muted">Company Profile</a></li>
          <li><a href="#sec-operations" class="block rounded-md px-3 py-2 text-sm hover:bg-muted">Operations Overview</a></li>
        </ul>
      </section>
    </nav>
    <div
      v-else
      class="lg:sticky lg:top-[72px] self-start space-y-6 animate-pulse"
    >
      <section class="rounded-lg border border-border bg-card p-4">
        <div class="h-4 w-32 rounded bg-muted"></div>
        <div class="mt-4 space-y-3">
          <div class="h-3 w-full rounded bg-muted"></div>
          <div class="h-3 w-3/4 rounded bg-muted"></div>
          <div class="h-3 w-2/4 rounded bg-muted"></div>
        </div>
      </section>
      <section class="rounded-lg border border-border bg-card p-4">
        <div class="h-3 w-1/2 rounded bg-muted"></div>
        <div class="mt-3 space-y-2">
          <div class="h-2.5 w-full rounded bg-muted"></div>
          <div class="h-2.5 w-5/6 rounded bg-muted"></div>
          <div class="h-2.5 w-4/6 rounded bg-muted"></div>
        </div>
      </section>
    </div>

    <!-- Editor Canvas (right) -->
    <main
      v-if="isChecklistReady"
      class="space-y-8"
    >
      <section id="sec-details" class="rounded-lg border border-border bg-card p-6">
        <h2 class="text-lg font-semibold mb-6">Company Details</h2>

        <!-- Logo Upload Area -->
        <div
          :class="[
            'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4',
            showLogoUploader ? 'mb-4' : 'mb-8'
          ]"
        >
          <div class="flex items-center gap-5">
            <!-- Larger logo box -->
            <div class="w-28 h-28 rounded-lg bg-muted flex items-center justify-center overflow-hidden border border-border">
              <img
                v-if="form.logo"
                :src="form.logo"
                alt="Company Logo"
                class="max-h-full max-w-full object-contain"
              />
              <span v-else class="text-xs text-muted-foreground">Logo</span>
            </div>

            <div>
              <h3 class="text-sm font-medium text-foreground">Company Logo</h3>
              <p class="text-xs text-muted-foreground">
                PNG, JPG or SVG up to 2 MB · Recommended ≥ 400×400 px
              </p>
            </div>
          </div>

          <button
            type="button"
            class="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium
                  bg-brand text-white hover:opacity-90 transition shadow-sm"
            @click="showLogoUploader = true"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Replace Logo
          </button>
        </div>

        <ProfileLogoUploader
          v-if="showLogoUploader"
          class="mt-2 mb-8"
          :current-logo="form.logo"
          @uploaded="handleLogoUploaded"
          @removed="handleLogoRemoved"
          @close="showLogoUploader = false"
        />

        <!-- Form Fields -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Company Name -->
          <label class="block md:col-span-2">
            <span class="text-sm font-medium">Company Name</span>
            <input
              v-model="form.name"
              @input="triggerAutosave"
              class="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-1 focus:ring-brand outline-none"
              placeholder="Acme Mining Corp"
            />
          </label>
          
          <!-- Industry -->
          <label class="block">
            <span class="text-sm font-medium">Industry</span>
            <select
              v-model="form.industry"
              :disabled="isLoadingIndustries"
              @change="handleIndustryChange"
              class="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-1 focus:ring-brand outline-none"
            >
              <option value="">Select industry</option>
              <option
                v-for="industry in industries"
                :key="industry.id"
                :value="industry.id"
              >
                {{ industry.name }}
              </option>
            </select>
          </label>

          <label v-if="subcategoryOptions.length" class="block">
            <span class="text-sm font-medium">Subcategory <span class="text-muted-foreground">(optional)</span></span>
            <select
              v-model.number="form.industry_subcategory_id"
              @change="handleSubcategoryChange"
              class="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-1 focus:ring-brand outline-none"
            >
              <option :value="null">Select subcategory</option>
              <option
                v-for="subcategory in subcategoryOptions"
                :key="subcategory.id"
                :value="subcategory.id"
              >
                {{ subcategory.name }}
              </option>
            </select>
          </label>

          <!-- Employee Size -->
          <label class="block">
            <span class="text-sm font-medium">Employee Size</span>
            <select
              v-model="form.employee_size"
              @change="triggerAutosave"
              class="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-1 focus:ring-brand outline-none"
            >
              <option value="">Select size</option>
              <option
                v-for="size in EMPLOYEE_SIZES"
                :key="size"
                :value="size"
              >
                {{ size }}
              </option>
            </select>
          </label>

          <label class="block">
            <span class="text-sm font-medium">Headcount Growth (Last 12 Months) <span class="text-muted-foreground">(%)</span></span>
            <input
              v-model="form.headcount_growth_12_months"
              @input="triggerAutosave"
              class="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-1 focus:ring-brand outline-none"
              placeholder="e.g., 12"
            />
          </label>

          <label class="block">
            <span class="text-sm font-medium">Average Employee Tenure <span class="text-muted-foreground">(years)</span></span>
            <input
              v-model="form.average_employee_tenure"
              @input="triggerAutosave"
              class="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-1 focus:ring-brand outline-none"
              placeholder="e.g., 3.5"
            />
          </label>

          <!-- Website -->
          <label class="block">
            <span class="text-sm font-medium">Website</span>
            <input
              v-model="form.website"
              @input="triggerAutosave"
              class="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-1 focus:ring-brand outline-none"
              placeholder="https://example.com"
            />
          </label>

          <!-- Head Office -->
          <label class="block">
            <span class="text-sm font-medium">Head Office</span>
            <input
              v-model="form.office_address"
              @input="triggerAutosave"
              class="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-1 focus:ring-brand outline-none"
              placeholder="Perth, WA"
            />
          </label>

          <!-- Company Email -->
          <label class="block md:col-span-2">
            <span class="text-sm font-medium">Company email <span class="text-muted-foreground">(optional)</span></span>
            <input
              v-model="form.contact_email"
              @input="triggerAutosave"
              type="email"
              class="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-1 focus:ring-brand outline-none"
              placeholder="contact@example.com"
            />
          </label>

          <!-- Summary -->
          <label class="block md:col-span-2">
            <span class="text-sm font-medium">Company Summary</span>
            <textarea
              v-model="form.summary"
              @input="triggerAutosave"
              class="mt-1 w-full min-h-[96px] rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-1 focus:ring-brand outline-none resize-none"
              placeholder="Describe your mission, operations, and key activities..."
            ></textarea>
          </label>
        </div>
      </section>


      <section id="sec-profile" class="rounded-lg border border-border bg-card p-6 mt-8">
        <h2 class="text-lg font-semibold mb-6">Company Profile</h2>

        <!-- Key People -->
        <div class="mb-8">
          <div class="flex items-center gap-2 mb-2">
            <label class="font-medium text-sm">Key People</label>
            <span class="text-muted-foreground text-xs">(executives, managers, etc.)</span>
          </div>
          <div class="space-y-3">
            <div
              v-for="(person, index) in form.key_people"
              :key="index"
              class="rounded-lg border border-border/60 bg-background/70 p-3 shadow-sm"
            >
              <div class="grid gap-2 sm:grid-cols-[repeat(4,minmax(0,1fr))] sm:items-start">
                <input
                  v-model="person.first_name"
                  @input="triggerAutosave"
                  type="text"
                  placeholder="First name"
                  class="border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
                />
                <input
                  v-model="person.last_name"
                  @input="triggerAutosave"
                  type="text"
                  placeholder="Last name"
                  class="border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
                />
                <input
                  v-model="person.role"
                  @input="triggerAutosave"
                  type="text"
                  placeholder="Role / Job Title"
                  class="border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:border-transparent outline-none sm:col-span-1"
                />
                <VueDatePicker
                  v-model="person.role_start_date"
                  :enable-time-picker="false"
                  auto-apply
                  teleport
                  model-type="yyyy-MM-dd"
                  format="dd/MM/yyyy"
                  placeholder="Role start date"
                  @update:model-value="triggerAutosave"
                  :input-class="[
                    'w-full h-10 rounded-md border border-border text-foreground px-3 text-sm focus:ring-2 focus:ring-brand focus:border-brand bg-background',
                  ]"
                  :menu-class="['rounded-lg shadow-lg border border-border bg-background']"
                />
              </div>
              <div
                v-if="form.key_people && form.key_people.length > 1"
                class="mt-3 flex justify-end"
              >
                <button
                  @click="removePerson(index)"
                  class="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground transition hover:border-brand hover:text-foreground"
                  type="button"
                >
                  <span>Remove</span>
                </button>
              </div>
            </div>

            <button
              type="button"
              @click="addPerson"
              class="w-full border border-dashed rounded-md py-2 text-sm text-muted-foreground hover:border-brand hover:text-foreground transition-colors"
            >
              + Add Person
            </button>
          </div>
        </div>

        <div class="mb-8">
          <div class="flex items-center gap-2 mb-2">
            <label class="font-medium text-sm">Executive Movements</label>
            <span class="text-muted-foreground text-xs">(optional)</span>
          </div>
          <p class="mb-4 text-sm text-muted-foreground">
            Highlight notable recent leadership appointments or resignations with a short summary and source link.
          </p>

          <div class="space-y-3">
            <div
              v-for="(hire, index) in form.recent_key_hires"
              :key="index"
              class="rounded-lg border border-border/60 bg-background/80 p-4 shadow-sm space-y-3"
            >
              <div class="grid gap-3 md:grid-cols-2">
                <input
                  v-model="hire.name"
                  @input="triggerAutosave"
                  type="text"
                  placeholder="Full name"
                  class="border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
                />
                <input
                  v-model="hire.role"
                  @input="triggerAutosave"
                  type="text"
                  placeholder="Role / Title"
                  class="border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
                />
                <input
                  v-model="hire.date"
                  @input="triggerAutosave"
                  type="date"
                  class="border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
                />
                <select
                  v-model="hire.background"
                  @change="triggerAutosave"
                  class="border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
                >
                  <option value="Appointment">Appointment</option>
                  <option value="Resignation">Resignation</option>
                </select>
                <input
                  v-model="hire.url"
                  @input="triggerAutosave"
                  type="url"
                  placeholder="Source URL"
                  class="border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:border-transparent outline-none md:col-span-2"
                />
                <textarea
                  v-model="hire.summary"
                  @input="triggerAutosave"
                  rows="3"
                  placeholder="Short summary"
                  class="border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:border-transparent outline-none resize-none md:col-span-2"
                ></textarea>
              </div>

              <div class="flex justify-end">
                <button
                  @click="removeRecentKeyHire(index)"
                  class="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground transition hover:border-brand hover:text-foreground"
                  type="button"
                >
                  <span>Remove</span>
                </button>
              </div>
            </div>

            <button
              type="button"
              @click="addRecentKeyHire"
              class="w-full border border-dashed rounded-md py-2 text-sm text-muted-foreground hover:border-brand hover:text-foreground transition-colors"
            >
              + Add Executive Movement
            </button>
          </div>
        </div>

        <!-- Commodities hidden for now; keep the markup nearby for easy recovery. -->
        <div
          v-if="false && shouldShowCommodities"
          class="mb-8"
        >
          <h3 class="text-sm font-medium mb-2">Commodities</h3>
          <div class="mt-3 flex flex-wrap gap-2">
            <button
              v-for="commodity in COMMODITIES"
              :key="commodity"
              type="button"
              @click="toggleCommodity(commodity)"
              class="rounded-full border px-3 py-1 text-xs transition-colors"
              :class="form.commodities?.includes(commodity)
                ? 'bg-brand text-white border-brand'
                : 'border-border text-muted-foreground hover:border-brand hover:text-foreground'"
            >
              {{ commodity }}
            </button>
          </div>
        </div>

        <!-- Industry-specific fields (e.g. construction sectors) -->
        <IndustryAttributeFields
          v-if="industryProfile?.fields.length"
          class="mb-8"
          :profile="industryProfile"
          :model-value="form.industry_attributes"
          :errors="industryAttributeFieldErrors"
          @update:model-value="handleIndustryAttributesUpdate"
        />

        <!-- Key Projects -->
        <div v-if="showKeyProjects" class="mb-8">
          <div class="flex items-center gap-2 mb-2">
            <label class="font-medium text-sm">Key Projects</label>
            <span class="text-muted-foreground text-xs">(optional)</span>
          </div>

          <div class="space-y-3">
            <div
              v-for="(project, index) in form.key_projects"
              :key="index"
              class="rounded-lg border border-border/60 bg-background/80 p-3 shadow-sm space-y-2"
            >
              <div class="flex flex-col sm:flex-row gap-2">
                <input
                  v-model="project.name"
                  type="text"
                  placeholder="Project Name"
                  @input="triggerAutosave"
                  class="flex-1 border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none text-sm"
                />
                <select
                  v-model="project.stage"
                  @change="triggerAutosave"
                  class="flex-1 border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none text-sm"
                >
                  <option value="">Select Stage</option>
                  <option v-for="stage in PROJECT_STAGES" :key="stage" :value="stage">{{ stage }}</option>
                </select>
                <button
                  v-if="form.key_projects && form.key_projects.length > 1"
                  @click="removeProject(index)"
                  type="button"
                  class="border border-border rounded-md px-2 hover:bg-muted text-sm"
                >
                  ✕
                </button>
              </div>

              <input
                v-model="project.location_label"
                type="text"
                placeholder="Project location"
                @input="triggerAutosave"
                class="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none text-sm"
              />

              <textarea
                v-model="project.description"
                rows="3"
                placeholder="Project description"
                @input="triggerAutosave"
                class="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none text-sm"
              ></textarea>

              <GoogleMap
                v-if="project.location_label"
                editable
                :address="project.location_label"
                :lat="project.location_lat ?? null"
                :lng="project.location_lng ?? null"
                map-height-class="h-40"
                @update:location="updateProjectLocation(index, $event)"
              />

              <div class="space-y-2 rounded-md border border-border/60 bg-white/70 p-3">
                <div class="flex items-center justify-between gap-2">
                  <p class="text-xs font-medium text-muted-foreground">Activity feed</p>
                  <button
                    type="button"
                    class="text-xs font-semibold text-brand hover:text-brand/80"
                    @click="addProjectActivity(index)"
                  >
                    + Add update
                  </button>
                </div>

                <div
                  v-for="(activity, activityIndex) in project.activity_feed"
                  :key="activityIndex"
                  class="grid gap-2 sm:grid-cols-[150px,minmax(0,1fr),auto]"
                >
                  <input
                    v-model="activity.date"
                    type="date"
                    @change="triggerAutosave"
                    class="border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none text-sm"
                  />
                  <textarea
                    v-model="activity.text"
                    rows="2"
                    placeholder="Market update"
                    @input="triggerAutosave"
                    class="border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none text-sm"
                  ></textarea>
                  <button
                    type="button"
                    class="border border-border rounded-md px-2 text-sm hover:bg-muted"
                    @click="removeProjectActivity(index, activityIndex)"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <p class="text-xs font-medium text-muted-foreground">Project commodities</p>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="commodity in COMMODITIES"
                  :key="commodity"
                  type="button"
                  @click="toggleProjectCommodity(index, commodity)"
                  class="rounded-full border px-3 py-1 text-xs transition-colors"
                  :class="project.commodities?.includes(commodity)
                    ? 'bg-brand text-white border-brand'
                    : 'border-border text-muted-foreground hover:border-brand hover:text-foreground'"
                >
                  {{ commodity }}
                </button>
              </div>
            </div>
            <button
              type="button"
              @click="addProject"
              class="w-full border border-dashed rounded-md py-2 text-sm text-muted-foreground hover:border-brand hover:text-foreground transition-colors"
            >
              + Add Project
            </button>
          </div>
        </div>

        <div class="mb-8">
          <div class="flex items-center gap-2 mb-2">
            <label class="font-medium text-sm">Job Links</label>
            <span class="text-muted-foreground text-xs">(optional)</span>
          </div>
          <p class="mb-4 text-sm text-muted-foreground">
            Add any hiring destinations you want contractors to see on your company page.
          </p>

          <div class="space-y-4">
            <label
              v-for="jobLinkField in JOB_LINK_FIELDS"
              :key="jobLinkField.type"
              class="block rounded-lg border border-border/60 bg-background/80 p-4 shadow-sm"
            >
              <div class="flex items-center justify-between gap-3">
                <span class="text-sm font-medium text-foreground">{{ jobLinkField.label }}</span>
                <span class="rounded-full bg-muted px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  {{ jobLinkField.badge }}
                </span>
              </div>
              <input
                :value="getJobLinkUrl(jobLinkField.type)"
                type="url"
                :placeholder="jobLinkField.placeholder"
                @input="handleJobLinkInput(jobLinkField.type, $event)"
                class="mt-3 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-1 focus:ring-brand outline-none"
              />
              <p class="mt-2 text-xs text-muted-foreground">
                {{ jobLinkField.helpText }}
              </p>
            </label>
          </div>
        </div>

        <!-- Market Data -->
        <div v-if="shouldShowMarketData">
          <h3 class="text-sm font-medium mb-3">Market Data</h3>
          <div class="grid grid-cols-1 gap-4">
            <div class="flex items-center justify-between rounded-lg border border-border bg-muted/10 px-4 py-3">
              <div>
                <p class="text-sm font-medium text-foreground">Private company</p>
                <p class="text-xs text-muted-foreground">Hide market data for private companies.</p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  v-model="form.is_private_company"
                  type="checkbox"
                  class="sr-only peer"
                  @change="handlePrivateToggle"
                />
                <div class="h-6 w-11 rounded-full border border-border bg-muted/40 transition-colors peer-checked:bg-brand peer-focus-visible:ring-2 peer-focus-visible:ring-brand/40"></div>
                <div class="absolute left-1 top-1 h-4 w-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5"></div>
              </label>
            </div>

            <label class="block">
              <span class="text-sm font-medium">ASX Ticker</span>
              <div class="relative mt-1">
                <input
                  v-model="form.ticker"
                  type="text"
                  maxlength="5"
                  inputmode="text"
                  placeholder="e.g., BHP"
                  :disabled="form.is_private_company"
                  @input="handleTickerInput"
                  class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-1 focus:ring-brand outline-none uppercase disabled:bg-muted/40 disabled:text-muted-foreground"
                />
                <svg
                  v-if="isFetchingMarketCap"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-brand"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M4 12a8 8 0 018-8"></path>
                </svg>
              </div>
              <p v-if="marketCapError" class="mt-1 text-xs text-red-600">{{ marketCapError }}</p>
              <p v-else class="mt-1 text-xs text-muted-foreground">
                Market cap is fetched automatically from the ASX ticker. If you’re not listed, mark the company as private.
              </p>
            </label>

            <div v-if="form.market_cap" class="block">
              <span class="text-sm font-medium">Market Cap (auto)</span>
              <div class="mt-1 w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-foreground">
                {{ form.market_cap }}
              </div>
            </div>

            <label class="block">
              <span class="text-sm font-medium">Cash in Bank (AUD)</span>
              <input
                v-model="form.cash_in_bank"
                type="text"
                inputmode="decimal"
                @input="handleCashInBankInput"
                class="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-1 focus:ring-brand outline-none text-right"
                placeholder="e.g. 2,000,000"
              />
              <div v-if="hasCashInBankValue" class="mt-3">
                <span class="block text-xs font-medium mb-1 text-muted-foreground">Date of Balance</span>
                <VueDatePicker
                  v-model="form.cash_in_bank_updated_at"
                  :enable-time-picker="false"
                  auto-apply
                  teleport
                  model-type="yyyy-MM-dd"
                  format="dd/MM/yyyy"
                  placeholder="Select date"
                  @update:model-value="triggerAutosave"
                  :input-class="[
                    'w-full h-10 rounded-md border border-border text-foreground px-3 text-sm focus:ring-2 focus:ring-brand focus:border-brand bg-background',
                  ]"
                  :menu-class="['rounded-lg shadow-lg border border-border bg-background']"
                />
              </div>
            </label>

            <label class="block">
              <span class="text-sm font-medium">Cash Runway</span>
              <input
                v-model="form.cash_runway"
                type="text"
                inputmode="decimal"
                @input="handleCashRunwayInput"
                class="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-1 focus:ring-brand outline-none"
                placeholder="e.g. 4 or 4.5"
              />
              <p v-if="cashRunwayError" class="mt-1 text-xs text-red-600">{{ cashRunwayError }}</p>
              <p class="mt-1 text-xs text-muted-foreground">
                Estimated time (in quarters) a company can operate using current cash, based on recent spend
              </p>
            </label>
          </div>

          <div class="mt-6">
            <div class="flex items-center gap-2 mb-2">
              <label class="font-medium text-sm">Recent Capital Raisings</label>
              <span class="text-muted-foreground text-xs">(optional)</span>
            </div>
            <p class="mb-4 text-sm text-muted-foreground">
              Track recent placements, raises, or other funding events with a short source-backed summary.
            </p>

            <div class="space-y-3">
              <div
                v-for="(raising, index) in form.recent_capital_raisings"
                :key="index"
                class="rounded-lg border border-border/60 bg-background/80 p-4 shadow-sm space-y-3"
              >
                <div class="grid gap-3 md:grid-cols-2">
                  <label class="block">
                    <span class="mb-1 block text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">Date</span>
                    <input
                      v-model="raising.date"
                      @input="triggerAutosave"
                      type="date"
                      class="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
                    />
                  </label>
                  <label class="block">
                    <span class="mb-1 block text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">Amount Raised (AUD)</span>
                    <input
                      v-model="raising.amount"
                      @input="handleCapitalRaisingAmountInput(index)"
                      type="text"
                      inputmode="decimal"
                      placeholder="e.g. 12,000,000"
                      class="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
                    />
                  </label>
                  <label class="block">
                    <span class="mb-1 block text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">Type</span>
                    <input
                      v-model="raising.label"
                      @input="triggerAutosave"
                      type="text"
                      placeholder="e.g. Placement"
                      class="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
                    />
                  </label>
                  <label class="block">
                    <span class="mb-1 block text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">Source URL</span>
                    <input
                      v-model="raising.url"
                      @input="triggerAutosave"
                      type="url"
                      placeholder="Announcement or source URL"
                      class="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
                    />
                  </label>
                  <label class="block md:col-span-2">
                    <span class="mb-1 block text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">Summary</span>
                    <textarea
                      v-model="raising.summary"
                      @input="triggerAutosave"
                      rows="3"
                      placeholder="Short summary"
                      class="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:border-transparent outline-none resize-none"
                    ></textarea>
                  </label>
                </div>

                <div class="flex justify-end">
                  <button
                    @click="removeCapitalRaising(index)"
                    class="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground transition hover:border-brand hover:text-foreground"
                    type="button"
                  >
                    <span>Remove</span>
                  </button>
                </div>
              </div>

              <button
                type="button"
                @click="addCapitalRaising"
                class="w-full border border-dashed rounded-md py-2 text-sm text-muted-foreground hover:border-brand hover:text-foreground transition-colors"
              >
                + Add Capital Raising
              </button>
            </div>
          </div>

          <div class="mt-6">
            <div class="flex items-center gap-2 mb-2">
              <label class="font-medium text-sm">Benefits</label>
              <span class="text-muted-foreground text-xs">(optional)</span>
            </div>
            <p class="mb-4 text-sm text-muted-foreground">
              Add contractor-facing benefits and where the information came from, for example Seek.
            </p>

            <div class="space-y-3">
              <div
                v-for="(benefit, index) in form.benefits"
                :key="index"
                class="rounded-lg border border-border/60 bg-background/80 p-4 shadow-sm space-y-3"
              >
                <div class="grid gap-3 md:grid-cols-2">
                  <label class="block">
                    <span class="mb-1 block text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">Heading</span>
                    <input
                      v-model="benefit.heading"
                      @input="triggerAutosave"
                      type="text"
                      placeholder="e.g. Salary packaging"
                      class="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
                    />
                  </label>
                  <label class="block">
                    <span class="mb-1 block text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">Source</span>
                    <input
                      v-model="benefit.source"
                      @input="triggerAutosave"
                      type="text"
                      placeholder="e.g. Seek"
                      class="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
                    />
                  </label>
                  <label class="block md:col-span-2">
                    <span class="mb-1 block text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">Short Description</span>
                    <textarea
                      v-model="benefit.description"
                      @input="triggerAutosave"
                      rows="3"
                      placeholder="Short description of the benefit"
                      class="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:border-transparent outline-none resize-none"
                    ></textarea>
                  </label>
                </div>

                <p
                  v-if="benefit.added_at"
                  class="text-xs text-muted-foreground"
                >
                  Added {{ formatDateAsDayMonthYear(benefit.added_at) }}
                </p>

                <div class="flex justify-end">
                  <button
                    @click="removeBenefit(index)"
                    class="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground transition hover:border-brand hover:text-foreground"
                    type="button"
                  >
                    <span>Remove</span>
                  </button>
                </div>
              </div>

              <button
                type="button"
                @click="addBenefit"
                class="w-full border border-dashed rounded-md py-2 text-sm text-muted-foreground hover:border-brand hover:text-foreground transition-colors"
              >
                + Add Benefit
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- ON-SITE OPERATIONS -->
      <section id="sec-operations" aria-labelledby="sec-operations-title" class="rounded-lg border border-border bg-card p-6">
        <h2 id="sec-operations-title" class="text-lg font-semibold">Operations Overview</h2>
        <div class="mt-4 grid gap-4">
          <label
            v-if="shouldShowMiningOperations"
            class="block"
          >
            <span class="text-sm font-medium">Field Roster</span>
            <textarea
              @input="triggerAutosave"
              v-model="form.field_roster"
              class="mt-1 min-h-24 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-1 focus:ring-brand outline-none"
              placeholder="e.g., 2 weeks on / 1 week off"
            ></textarea>
          </label>
          <label class="block">
            <span class="text-sm font-medium">Typical Hours</span>
            <input @input="triggerAutosave" v-model="form.typical_hours" class="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-1 focus:ring-brand outline-none" placeholder="e.g., 12-hour shifts" />
          </label>
          <label
            v-if="shouldShowMiningOperations"
            class="block"
          >
            <span class="text-sm font-medium">Facilities</span>
            <textarea
              @input="triggerAutosave"
              v-model="form.facilities"
              class="mt-1 min-h-24 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-1 focus:ring-brand outline-none"
              placeholder="Accommodation camps, processing plant..."
            ></textarea>
          </label>
          <div>
            <span class="text-sm font-medium">Software Used</span>
            <TagAutocomplete
              v-model="softwareSelection"
              :options="softwareOptions"
              placeholder="Search software or add your own"
              clearable
            />
          </div>
        </div>
      </section>

      <section class="rounded-lg border border-border bg-card p-6">
        <div class="flex items-center gap-2 mb-2">
          <label class="font-medium text-sm">Social Media</label>
          <span class="text-muted-foreground text-xs">(optional)</span>
        </div>
        <p class="mb-4 text-sm text-muted-foreground">
          Add the main social channels you want candidates to use when researching your company.
        </p>

        <div class="space-y-4">
          <label
            v-for="socialField in SOCIAL_LINK_FIELDS"
            :key="socialField.type"
            class="block rounded-lg border border-border/60 bg-background/80 p-4 shadow-sm"
          >
            <span class="text-sm font-medium text-foreground">{{ socialField.label }}</span>
            <input
              :value="getSocialLinkUrl(socialField.type)"
              type="url"
              :placeholder="socialField.placeholder"
              @input="handleSocialLinkInput(socialField.type, $event)"
              class="mt-3 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:ring-1 focus:ring-brand outline-none"
            />
            <p class="mt-2 text-xs text-muted-foreground">
              {{ socialField.helpText }}
            </p>
          </label>
        </div>
      </section>

      <!-- Spacer bottom -->
      <div class="h-8"></div>
    </main>
    <div
      v-else
      class="space-y-8 animate-pulse"
    >
      <section class="rounded-lg border border-border bg-card p-6">
        <div class="h-5 w-40 rounded bg-muted"></div>
        <div class="mt-6 grid gap-4 md:grid-cols-2">
          <div class="h-4 w-full rounded bg-muted"></div>
          <div class="h-4 w-full rounded bg-muted"></div>
          <div class="md:col-span-2 space-y-4">
            <div class="h-3 w-full rounded bg-muted"></div>
            <div class="h-3 w-3/4 rounded bg-muted"></div>
            <div class="h-3 w-1/2 rounded bg-muted"></div>
          </div>
        </div>
      </section>
      <section class="rounded-lg border border-border bg-card p-6">
        <div class="h-5 w-48 rounded bg-muted"></div>
        <div class="mt-6 space-y-4">
          <div class="h-3 w-full rounded bg-muted"></div>
          <div class="h-3 w-5/6 rounded bg-muted"></div>
          <div class="h-3 w-2/3 rounded bg-muted"></div>
        </div>
      </section>
      <section class="rounded-lg border border-border bg-card p-6">
        <div class="h-5 w-56 rounded bg-muted"></div>
        <div class="mt-6 space-y-3">
          <div class="h-3 w-full rounded bg-muted"></div>
          <div class="h-3 w-5/6 rounded bg-muted"></div>
          <div class="h-3 w-4/6 rounded bg-muted"></div>
        </div>
      </section>
    </div>



  </div>

  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="opacity-0 -translate-y-3 scale-[0.98]"
    enter-to-class="opacity-100 translate-y-0 scale-100"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 -translate-y-2"
  >
    <div
      v-if="publishNotice"
      class="pointer-events-none fixed inset-x-0 top-4 z-[999] flex justify-center px-4"
    >
      <div
        class="pointer-events-auto relative flex w-full max-w-xl gap-4 rounded-2xl border border-border/60 bg-background/95 p-5 shadow-xl shadow-brand/10 backdrop-blur-md ring-1 ring-brand/10"
        role="alert"
        aria-live="assertive"
      >
        <div class="flex h-12 w-12 items-center justify-center rounded-full bg-brand/10 text-brand">
          <Sparkles class="h-6 w-6" />
        </div>
        <div class="flex-1 text-left">
          <p class="text-sm font-semibold text-foreground">
            {{ publishNotice.headline }}
          </p>
          <p class="mt-1 text-sm text-muted-foreground">
            {{ publishNotice.message }}
          </p>
          <div
            v-if="publishNotice.chips.length"
            class="mt-3 flex flex-wrap gap-2"
          >
            <button
              v-for="chip in publishNotice.chips"
              :key="chip.id"
              type="button"
              class="group inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand/10 px-3 py-1 text-xs font-medium text-brand transition hover:border-brand/30 hover:bg-brand/15"
              @click="handleChipClick(chip)"
            >
              <span class="h-1.5 w-1.5 rounded-full bg-brand transition group-hover:bg-brand/80"></span>
              <span>{{ chip.label }}</span>
            </button>
          </div>
        </div>
        <div class="flex flex-col items-end gap-3 text-right">
          <button
            type="button"
            class="rounded-full p-1 text-muted-foreground transition hover:bg-muted/40 hover:text-foreground"
            @click="dismissPublishNotice"
            aria-label="Dismiss publish warning"
          >
            <X class="h-4 w-4" />
          </button>
          <button
            v-if="publishNotice.primaryAnchor"
            type="button"
            class="inline-flex items-center gap-2 rounded-full bg-brand px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-brand/90"
            @click="handleReviewIssues"
          >
            Review now
            <ArrowRight class="h-3.5 w-3.5" />
          </button>
        </div>
        <span class="pointer-events-none absolute inset-x-10 -bottom-2 h-[3px] rounded-full bg-brand/40"></span>
      </div>
    </div>
  </Transition>

  <Transition
    enter-active-class="transition duration-200 ease-out"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition duration-150 ease-in"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="showDiscardModal"
      class="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 px-4"
      @click.self="closeDiscardModal"
    >
      <div class="w-full max-w-md rounded-lg border border-border bg-card shadow-2xl">
        <div class="px-6 py-5">
          <h3 class="text-lg font-semibold text-foreground">Discard draft?</h3>
          <p class="mt-2 text-sm text-muted-foreground">
            This will delete your saved draft and reload the latest published company profile.
          </p>
          <p v-if="discardError" class="mt-3 text-sm text-red-500">
            {{ discardError }}
          </p>
        </div>
        <div class="flex items-center justify-end gap-3 border-t border-border bg-muted/20 px-6 py-4">
          <button
            type="button"
            class="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            @click="closeDiscardModal"
            :disabled="isDiscarding"
          >
            Cancel
          </button>
          <button
            type="button"
            class="inline-flex items-center gap-2 rounded-md bg-destructive px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-destructive/90 disabled:opacity-60 disabled:cursor-not-allowed"
            @click="confirmDiscard"
            :disabled="isDiscarding"
          >
            <Loader2 v-if="isDiscarding" class="h-4 w-4 animate-spin" />
            <span>{{ isDiscarding ? 'Discarding…' : 'Discard draft' }}</span>
          </button>
        </div>
      </div>
    </div>
  </Transition>

  <Transition
    enter-active-class="transition duration-200 ease-out"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition duration-150 ease-in"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="showPublishModal"
      class="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 px-4"
      @click.self="closePublishModal"
    >
      <div class="w-full max-w-xl rounded-lg border border-border bg-card shadow-2xl">
        <div class="px-6 py-5">
          <div class="flex items-center gap-3">
            <div class="flex h-10 w-10 items-center justify-center rounded-full bg-brand/10 text-brand">
              <Sparkles class="h-5 w-5" />
            </div>
            <div>
              <h3 class="text-lg font-semibold text-foreground">Publish company profile?</h3>
              <p class="text-sm text-muted-foreground">
                Your updates will go live immediately and the draft will be cleared.
              </p>
            </div>
          </div>
          <ul class="mt-4 space-y-2 rounded-md border border-border/60 bg-background/60 p-4 text-sm text-muted-foreground">
            <li class="flex items-center gap-2">
              <CheckCircle2 class="h-4 w-4 text-brand" />
              All required sections look good.
            </li>
            <li class="flex items-center gap-2">
              <CheckCircle2 class="h-4 w-4 text-brand" />
              We’ll overwrite your published profile and remove the draft.
            </li>
          </ul>
          <p v-if="publishError" class="mt-3 text-sm text-red-500">
            {{ publishError }}
          </p>
        </div>
        <div class="flex items-center justify-end gap-3 border-t border-border bg-muted/20 px-6 py-4">
          <button
            type="button"
            class="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            @click="closePublishModal"
            :disabled="isPublishing"
          >
            Cancel
          </button>
          <button
            type="button"
            class="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-brand/90 disabled:opacity-60 disabled:cursor-not-allowed"
            @click="confirmPublish"
            :disabled="isPublishing"
          >
            <Loader2 v-if="isPublishing" class="h-4 w-4 animate-spin" />
            <span>{{ isPublishing ? 'Publishing…' : 'Publish profile' }}</span>
          </button>
        </div>
      </div>
    </div>
  </Transition>

  <SaveStatusToast :status="saveStatus" />
</div>
</template>


<script setup lang="ts">
import { ref, computed, onMounted, toRef, toRaw, onBeforeUnmount } from 'vue'
import { storeToRefs } from 'pinia'

import { useSaveStatus } from '@/composables/useSaveStatus'
import SaveStatusToast from '@/components/marketplace/company/profile/SaveStatusToast.vue'
import ProfileLogoUploader from '@/components/marketplace/company/profile/ProfileLogoUploader.vue'
import { useCompanyProfile } from '@/composables/useCompanyProfile'
import { useCompanyProfileForm } from '@/composables/useCompanyProfileForm'
import { useIndustryStore } from '@/stores/industryStore'
import { useCompanyIndustryProfileStore } from '@/stores/companyIndustryProfileStore'
import { useCompanyMarketData } from '@/composables/useCompanyMarketData'
import { getCompanyIndustryOptions, getCompanyMarketData } from '@/api/company/profile'
import { EMPLOYEE_SIZES, COMMODITIES, PROJECT_STAGES, SOFTWARE_LOOKUPS } from '@/constants/lookups'
import { formatDateAsDayMonthYear } from '@/utils/formatters'
import type { CompanyIndustryAttributes, CompanyIndustryOption, CompanyIndustrySubcategory } from '@/types/company'
import { CheckCircle2, Circle, AlertCircle, Sparkles, ArrowRight, X, Loader2 } from 'lucide-vue-next'
import TagAutocomplete from '@/components/inputs/TagAutocomplete.vue'
import GoogleMap from '@/components/marketplace/generic/GoogleMap.vue'
import IndustryAttributeFields from '@/components/marketplace/company/IndustryAttributeFields.vue'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'

type PublishNoticeChip = {
  id: string
  label: string
  anchor?: string
}

type PublishNoticeState = {
  headline: string
  message: string
  chips: PublishNoticeChip[]
  primaryAnchor?: string
}

type JobLinkType = 'linkedin' | 'seek' | 'other'
type SocialLinkType = 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube'

const industryStore = useIndustryStore()
const { industries, isLoading: isLoadingIndustries } = storeToRefs(industryStore)
const industryProfileStore = useCompanyIndustryProfileStore()
const companyIndustryOptions = ref<CompanyIndustryOption[]>([])

const { profile, loadProfile, saveDraft, discardDraft, publishProfile, isLoading, error } =
  useCompanyProfile()
const {
  form,
  isDirty,
  resetForm,
  validationErrors,
  isPublishAttempted,
  validateForPublish,
  preparePublishPayload,
  clearValidationState,
  getFirstErrorForFields,
  checklistSections,
  checklistProgress,
  isChecklistReady,
  industryProfile,
} = useCompanyProfileForm()
// const { saveStatus, triggerAutosave } = useSaveStatus()

// const { saveStatus, triggerAutosave } = useSaveStatus(() => saveDraft(form))
const { saveStatus, triggerAutosave } = useSaveStatus(async () => {
  await saveDraft({ ...toRaw(form) })
})

const statusLabel = computed(() => {
  switch (saveStatus.value) {
    case 'unsaved':
      return 'Unsaved changes'
    case 'saving':
      return 'Saving…'
    case 'error':
      return 'Save failed'
    default:
      return 'All changes saved'
  }
})

const hasDraft = computed(() => profile.value?.is_draft === true)
const isAutosavePending = computed(() => saveStatus.value === 'unsaved' || saveStatus.value === 'saving')
const canPublish = computed(
  () => hasDraft.value && !isAutosavePending.value && !isPublishing.value && !isLoading.value
)

const showDiscardModal = ref(false)
const showPublishModal = ref(false)
const isDiscarding = ref(false)
const isPublishing = ref(false)
const discardError = ref<string | null>(null)
const publishError = ref<string | null>(null)

const handleDiscardClick = () => {
  discardError.value = null
  showDiscardModal.value = true
}

const closeDiscardModal = () => {
  if (isDiscarding.value) return
  showDiscardModal.value = false
  discardError.value = null
}

const confirmDiscard = async () => {
  if (isDiscarding.value) return

  discardError.value = null
  isDiscarding.value = true
  try {
    await discardDraft()
    clearValidationState()
    resetForm()
    saveStatus.value = 'saved'
    dismissPublishNotice()
    showDiscardModal.value = false
  } catch (err: any) {
    discardError.value = err?.response?.data?.error ?? 'Failed to discard draft. Please try again.'
  } finally {
    isDiscarding.value = false
  }
}

const selectedIndustry = computed(() => {
  const industryValue = form.industry
  if (industryValue === '' || industryValue == null) return null

  const allIndustries = industries.value ?? []
  const asNumber = typeof industryValue === 'number' ? industryValue : Number(industryValue)

  if (!Number.isNaN(asNumber)) {
    const matchById = allIndustries.find((industry) => industry.id === asNumber)
    if (matchById) return matchById
  }

  if (typeof industryValue === 'string') {
    const normalized = industryValue.trim().toLowerCase()
    if (!normalized) return null
    const matchByName = allIndustries.find(
      (industry) => industry.name?.toLowerCase() === normalized
    )
    if (matchByName) return matchByName
  }

  return null
})

const resolvedIndustryName = computed(() => {
  if (selectedIndustry.value?.name) {
    return selectedIndustry.value.name
  }

  const industryValue = form.industry
  if (industryValue == null || industryValue === '') return ''

  if (typeof industryValue === 'number') {
    const matchById = industries.value.find((industry) => industry.id === industryValue)
    return matchById?.name ?? ''
  }

  if (typeof industryValue === 'string') {
    return industryValue.trim()
  }

  return ''
})

const normalizedIndustryForRules = computed(() => {
  const candidate = resolvedIndustryName.value.trim()
  if (candidate) return candidate.toLowerCase()

  const profileIndustry =
    typeof profile.value?.industry === 'string'
      ? profile.value.industry.trim().toLowerCase()
      : ''

  return profileIndustry
})

const industrySoftwareKey = computed(() => {
  const normalizedName = normalizedIndustryForRules.value
  if (normalizedName.includes('mining')) {
    return 'mining'
  }

  return 'default'
})

const shouldShowCommodities = computed(() => normalizedIndustryForRules.value.includes('mining'))

const shouldShowMiningOperations = computed(() => industryProfile.value?.sections.site_operations ?? false)

const shouldShowMarketData = computed(() => industryProfile.value?.sections.market_data ?? false)

const showKeyProjects = computed(() => industryProfile.value?.sections.key_projects ?? true)

const industryAttributeFieldErrors = computed(() =>
  Object.fromEntries(
    (industryProfile.value?.fields ?? [])
      .map((field) => [field.key, getFirstErrorForFields([`industry_attributes.${field.key}`])])
      .filter(([, message]) => message)
  ) as Record<string, string>
)

const handleIndustryAttributesUpdate = (value: CompanyIndustryAttributes) => {
  form.industry_attributes = value
  triggerAutosave()
}

const selectedCompanyIndustryOption = computed<CompanyIndustryOption | null>(() => {
  const normalized = resolvedIndustryName.value.trim().toLowerCase()
  if (!normalized) return null

  return companyIndustryOptions.value.find((option) => {
    const name = option.name.trim().toLowerCase()
    const slug = option.slug.trim().toLowerCase()
    return normalized === name || normalized === slug || normalized.includes(name) || name.includes(normalized)
  }) ?? null
})

const subcategoryOptions = computed<CompanyIndustrySubcategory[]>(() =>
  selectedCompanyIndustryOption.value?.subcategories ?? []
)

const softwareOptions = computed(() => {
  const key = industrySoftwareKey.value
  const options = SOFTWARE_LOOKUPS[key] ?? SOFTWARE_LOOKUPS.default
  return [...options]
})

const softwareSelection = computed<string[]>({
  get() {
    return Array.isArray(form.software_used) ? form.software_used : []
  },
  set(value) {
    form.software_used = value
    triggerAutosave()
  },
})

const syncIndustrySubcategory = () => {
  const selected = subcategoryOptions.value.find(
    (subcategory) => subcategory.id === Number(form.industry_subcategory_id)
  )

  if (!selected) {
    form.industry_subcategory_id = null
    form.industry_subcategory = ''
    return
  }

  form.industry_subcategory = selected.name
}

const handleIndustryChange = () => {
  syncIndustrySubcategory()
  triggerAutosave()
}

const handleSubcategoryChange = () => {
  syncIndustrySubcategory()
  triggerAutosave()
}

const hasCashInBankValue = computed(
  () => typeof form.cash_in_bank === 'string' && form.cash_in_bank.trim() !== ''
)

const cashRunwayError = computed(() => getFirstErrorForFields(['cash_runway']))

const tickerRef = toRef(form, 'ticker')
const marketCapRef = toRef(form, 'market_cap')
const showLogoUploader = ref(false)
const publishNotice = ref<PublishNoticeState | null>(null)

const sectionAnchors: Record<string, string> = {
  details: '#sec-details',
  profile: '#sec-profile',
  operations: '#sec-operations',
}

const JOB_LINK_FIELDS: Array<{
  type: JobLinkType
  label: string
  badge: string
  placeholder: string
  helpText: string
  title: string
  description: string
}> = [
  {
    type: 'linkedin',
    label: 'LinkedIn Jobs URL',
    badge: 'LinkedIn',
    placeholder: 'https://www.linkedin.com/company/your-company/jobs/',
    helpText: 'Use your company LinkedIn jobs page or a LinkedIn job search filtered to your company.',
    title: 'LinkedIn Jobs',
    description: 'Browse featured roles, team updates, and quick-apply opportunities on LinkedIn.',
  },
  {
    type: 'seek',
    label: 'SEEK Jobs URL',
    badge: 'SEEK',
    placeholder: 'https://www.seek.com.au/your-company-jobs',
    helpText: 'Link to your SEEK company listing or your SEEK job search results.',
    title: 'SEEK Listings',
    description: 'View advertised vacancies on SEEK, including site-based and corporate roles.',
  },
  {
    type: 'other',
    label: 'Other Careers URL',
    badge: 'Other',
    placeholder: 'https://yourcompany.com/careers',
    helpText: 'Use this for your own careers page or any other external hiring destination.',
    title: 'Company Careers Page',
    description: 'Visit the company’s own careers hub for direct applications and employer branding.',
  },
]

const SOCIAL_LINK_FIELDS: Array<{
  type: SocialLinkType
  label: string
  placeholder: string
  helpText: string
}> = [
  {
    type: 'linkedin',
    label: 'LinkedIn',
    placeholder: 'https://www.linkedin.com/company/your-company',
    helpText: 'Use your main company LinkedIn page.',
  },
  {
    type: 'x',
    label: 'X',
    placeholder: 'https://x.com/yourcompany',
    helpText: 'Add your X profile if you actively share company updates there.',
  },
  {
    type: 'facebook',
    label: 'Facebook',
    placeholder: 'https://www.facebook.com/yourcompany',
    helpText: 'Use your public company Facebook page.',
  },
  {
    type: 'instagram',
    label: 'Instagram',
    placeholder: 'https://www.instagram.com/yourcompany',
    helpText: 'Use your Instagram profile if it reflects your employer brand.',
  },
  {
    type: 'youtube',
    label: 'YouTube',
    placeholder: 'https://www.youtube.com/@yourcompany',
    helpText: 'Link to your company YouTube channel for project, team, or brand content.',
  },
]

const pluralize = (count: number, singular: string, plural?: string) => {
  const resolvedPlural = plural ?? `${singular}s`
  return count === 1 ? singular : resolvedPlural
}

const buildPublishNotice = (): PublishNoticeState => {
  const sections = checklistSections.value ?? []
  const issues = sections.flatMap((section) =>
    section.items
      .filter((item) => !item.complete && (item.required || item.message))
      .map((item) => ({
        id: `${section.id}-${item.id}`,
        label: item.label,
        sectionLabel: section.label,
        required: item.required,
        anchor: sectionAnchors[section.id],
      }))
  )

  const requiredIssues = issues.filter((issue) => issue.required)
  const focusGroup = requiredIssues.length ? requiredIssues : issues
  const totalRequired = requiredIssues.length
  const totalIssues = issues.length

  const headline = !issues.length
    ? 'Review your profile before publishing'
    : totalRequired
      ? 'A few essentials still need your input'
      : 'Almost there—polish the final details'

  const messageParts = [
    !issues.length
      ? 'Update the checklist items highlighted in red on the left to continue.'
      : totalRequired
        ? `We noticed ${totalRequired} required ${pluralize(totalRequired, 'section')} that still need a quick update.`
        : `We noticed ${totalIssues} ${pluralize(totalIssues, 'detail')} that still need a quick update.`,
    'Everything flagged in red on the checklist will guide you step by step.',
  ]

  const chips = focusGroup.slice(0, 3).map((issue) => ({
    id: issue.id,
    label: `${issue.sectionLabel} · ${issue.label}`,
    anchor: issue.anchor,
  }))

  return {
    headline,
    message: messageParts.join(' '),
    chips,
    primaryAnchor: focusGroup[0]?.anchor,
  }
}

const scrollToAnchor = (anchor?: string) => {
  if (!anchor || typeof window === 'undefined') return
  const target = document.querySelector(anchor)
  if (target instanceof HTMLElement) {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

let publishNoticeTimer: ReturnType<typeof setTimeout> | null = null
const SHOW_NOTICE_DURATION = 7000

const showPublishNotice = () => {
  publishNotice.value = buildPublishNotice()
  if (publishNoticeTimer) {
    clearTimeout(publishNoticeTimer)
  }
  publishNoticeTimer = setTimeout(() => {
    publishNotice.value = null
  }, SHOW_NOTICE_DURATION)
}

const dismissPublishNotice = () => {
  if (publishNoticeTimer) {
    clearTimeout(publishNoticeTimer)
    publishNoticeTimer = null
  }
  publishNotice.value = null
}

const handleReviewIssues = () => {
  scrollToAnchor(publishNotice.value?.primaryAnchor)
  dismissPublishNotice()
}

const handleChipClick = (chip: PublishNoticeChip) => {
  scrollToAnchor(chip.anchor)
  dismissPublishNotice()
}

const {
  isFetching: isFetchingMarketCap,
  error: marketCapError,
  onTickerInput: handleTickerInputInner,
} = useCompanyMarketData(tickerRef, marketCapRef, {
  fetcher: async (symbol) => {
    try {
      const { data } = await getCompanyMarketData(symbol)
      return data
    } catch (err: any) {
      if (err?.response?.status === 404) {
        return { notFound: true }
      }
      throw err
    }
  },
  onResult: (_result, meta) => {
    if (meta.fromUser) {
      triggerAutosave()
    }
  },
})

const handleTickerInput = (event: Event) => {
  if (form.is_private_company) return
  handleTickerInputInner(event)
  triggerAutosave()
}

const normalizeAmountDisplay = (value: unknown): string => {
  const digits = String(value ?? '').replace(/[^\d]/g, '')
  if (!digits) return ''

  return new Intl.NumberFormat('en-AU').format(Number(digits))
}

const normalizeDecimalInput = (value: unknown): string => {
  const raw = String(value ?? '').replace(/[^\d.]/g, '')
  const [whole = '', ...decimalParts] = raw.split('.')
  if (decimalParts.length === 0) return whole
  return `${whole}.${decimalParts.join('')}`
}

const handlePrivateToggle = () => {
  if (form.is_private_company) {
    form.ticker = ''
    form.market_cap = ''
  }
  triggerAutosave()
}

const handleCashInBankInput = () => {
  form.cash_in_bank = normalizeAmountDisplay(form.cash_in_bank)
  form.cash_in_bank_updated_at = null
  triggerAutosave()
}

const handleCashRunwayInput = () => {
  form.cash_runway = normalizeDecimalInput(form.cash_runway)
  triggerAutosave()
}

const handleCapitalRaisingAmountInput = (index: number) => {
  if (!form.recent_capital_raisings) return
  const raising = form.recent_capital_raisings[index]
  if (!raising) return

  raising.amount = normalizeAmountDisplay(raising.amount)
  triggerAutosave()
}

const handleLogoUploaded = ({ url, fileType }: { url: string; fileType: string }) => {
  form.logo = url
  form.logo_type = fileType
  triggerAutosave()
  showLogoUploader.value = false
}

const handleLogoRemoved = () => {
  form.logo = ''
  form.logo_type = null
  triggerAutosave()
}

const closePublishModal = () => {
  if (isPublishing.value) return
  showPublishModal.value = false
  publishError.value = null
}

const handlePublish = () => {
  const result = validateForPublish()
  if (!result.success) {
    showPublishNotice()
    return
  }

  publishError.value = null
  showPublishModal.value = true
}

const confirmPublish = async () => {
  if (isPublishing.value) return

  publishError.value = null
  isPublishing.value = true

  try {
    const payload = preparePublishPayload()
    await publishProfile(payload)
    clearValidationState()
    resetForm()
    saveStatus.value = 'saved'
    dismissPublishNotice()
    showPublishModal.value = false
  } catch (err: any) {
    if (err instanceof Error && err.message === 'Validation failed') {
      showPublishNotice()
      publishError.value = 'Please resolve the highlighted fields before publishing.'
    } else {
      publishError.value = err?.response?.data?.error ?? 'Failed to publish profile. Please try again.'
    }
  } finally {
    isPublishing.value = false
  }
}

const toggleCommodity = (commodity: string) => {
  if (!form.commodities) form.commodities = []

  if (form.commodities.includes(commodity)) {
    form.commodities = form.commodities.filter((c) => c !== commodity)
  } else {
    form.commodities.push(commodity)
  }

  triggerAutosave()
}

const getJobLinkUrl = (type: JobLinkType): string => {
  if (!Array.isArray(form.job_links)) return ''
  return form.job_links.find((jobLink) => jobLink.type === type)?.url ?? ''
}

const getSocialLinkUrl = (type: SocialLinkType): string => {
  if (!Array.isArray(form.social_links)) return ''
  return form.social_links.find((socialLink) => socialLink.type === type)?.url ?? ''
}

const handleJobLinkInput = (type: JobLinkType, event: Event) => {
  const target = event.target
  const value = target instanceof HTMLInputElement ? target.value : ''
  updateJobLinkUrl(type, value)
}

const updateJobLinkUrl = (type: JobLinkType, rawValue: string) => {
  if (!Array.isArray(form.job_links)) {
    form.job_links = []
  }

  const value = rawValue.trim()
  const fieldConfig = JOB_LINK_FIELDS.find((field) => field.type === type)
  if (!fieldConfig) return

  const existingIndex = form.job_links.findIndex((jobLink) => jobLink.type === type)

  if (value === '') {
    if (existingIndex !== -1) {
      form.job_links.splice(existingIndex, 1)
    }
    triggerAutosave()
    return
  }

  const nextEntry = {
    type,
    title: fieldConfig.title,
    description: fieldConfig.description,
    url: value,
  }

  if (existingIndex === -1) {
    form.job_links.push(nextEntry)
  } else {
    form.job_links[existingIndex] = nextEntry
  }

  triggerAutosave()
}

const handleSocialLinkInput = (type: SocialLinkType, event: Event) => {
  const target = event.target
  const value = target instanceof HTMLInputElement ? target.value : ''
  updateSocialLinkUrl(type, value)
}

const updateSocialLinkUrl = (type: SocialLinkType, rawValue: string) => {
  if (!Array.isArray(form.social_links)) {
    form.social_links = []
  }

  const value = rawValue.trim()
  const existingIndex = form.social_links.findIndex((socialLink) => socialLink.type === type)

  if (value === '') {
    if (existingIndex !== -1) {
      form.social_links.splice(existingIndex, 1)
    }
    triggerAutosave()
    return
  }

  const nextEntry = {
    type,
    url: value,
  }

  if (existingIndex === -1) {
    form.social_links.push(nextEntry)
  } else {
    form.social_links[existingIndex] = nextEntry
  }

  triggerAutosave()
}

const addPerson = () => {
  if (!form.key_people) form.key_people = []
  form.key_people.push({ first_name: '', last_name: '', role: '', role_start_date: '' })
  triggerAutosave()
}

const removePerson = (index: number) => {
  if (!form.key_people) return
  form.key_people.splice(index, 1)
  triggerAutosave()
}

const addProject = () => {
  if (!form.key_projects) form.key_projects = []
  form.key_projects.push({
    name: '',
    stage: '',
    description: '',
    location_label: '',
    location_lat: null,
    location_lng: null,
    commodities: [],
    activity_feed: [],
  })
  triggerAutosave()
}

const removeProject = (index: number) => {
  if (!form.key_projects) return
  form.key_projects.splice(index, 1)
  triggerAutosave()
}

const addRecentKeyHire = () => {
  if (!form.recent_key_hires) form.recent_key_hires = []
  form.recent_key_hires.push({
    name: '',
    role: '',
    date: '',
    background: 'Appointment',
    summary: '',
    url: '',
  })
  triggerAutosave()
}

const removeRecentKeyHire = (index: number) => {
  if (!form.recent_key_hires) return
  form.recent_key_hires.splice(index, 1)
  triggerAutosave()
}

const addCapitalRaising = () => {
  if (!form.recent_capital_raisings) form.recent_capital_raisings = []
  form.recent_capital_raisings.push({
    date: '',
    amount: '',
    label: '',
    summary: '',
    url: '',
  })
  triggerAutosave()
}

const removeCapitalRaising = (index: number) => {
  if (!form.recent_capital_raisings) return
  form.recent_capital_raisings.splice(index, 1)
  triggerAutosave()
}

const addBenefit = () => {
  if (!form.benefits) form.benefits = []

  const now = new Date()
  const localDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000)

  form.benefits.push({
    heading: '',
    description: '',
    source: '',
    added_at: localDate.toISOString().slice(0, 10),
  })
  triggerAutosave()
}

const removeBenefit = (index: number) => {
  if (!form.benefits) return
  form.benefits.splice(index, 1)
  triggerAutosave()
}

const toggleProjectCommodity = (index: number, commodity: string) => {
  if (!form.key_projects) return
  const project = form.key_projects[index]
  if (!project) return
  if (!Array.isArray(project.commodities)) {
    project.commodities = []
  }
  if (project.commodities.includes(commodity)) {
    project.commodities = project.commodities.filter((item) => item !== commodity)
  } else {
    project.commodities.push(commodity)
  }
  triggerAutosave()
}

const addProjectActivity = (index: number) => {
  if (!form.key_projects) return
  const project = form.key_projects[index]
  if (!project) return
  if (!Array.isArray(project.activity_feed)) {
    project.activity_feed = []
  }
  project.activity_feed.push({ date: '', text: '' })
  triggerAutosave()
}

const removeProjectActivity = (projectIndex: number, activityIndex: number) => {
  if (!form.key_projects) return
  const project = form.key_projects[projectIndex]
  if (!project || !Array.isArray(project.activity_feed)) return
  project.activity_feed.splice(activityIndex, 1)
  triggerAutosave()
}

const updateProjectLocation = (index: number, location: { lat: number; lng: number }) => {
  if (!form.key_projects) return
  const project = form.key_projects[index]
  if (!project) return
  project.location_lat = location.lat
  project.location_lng = location.lng
  triggerAutosave()
}

onMounted(() => {
  loadProfile()
  industryStore.fetchIndustries()
  industryProfileStore.fetchProfiles()
  getCompanyIndustryOptions()
    .then(({ data }) => {
      companyIndustryOptions.value = Array.isArray(data?.data) ? data.data : []
    })
    .catch((err) => {
      console.error('Failed to fetch company industry options', err)
    })
});

onBeforeUnmount(() => {
  if (publishNoticeTimer) {
    clearTimeout(publishNoticeTimer)
  }
})
</script>

<style lang="css" scoped>
section[id^="sec-"] {
  scroll-margin-top: 80px; /* Adjust to match your header height */
}
</style>
